import { RefObject, useCallback, useEffect, useRef } from 'react';

import { DiscussionItem } from '../../../../resources/discussion/types';

function getMessageCount(messages: Record<string, DiscussionItem[]>): number {
    return Object.values(messages).flat().length;
}

export function useDiscussionAutoScroll(
    discussionRef: RefObject<HTMLDivElement | null>,
    messages: Record<string, DiscussionItem[]>,
    sendingMessageIds: string[],
) {
    const scrollToBottom = useCallback(() => {
        const element = discussionRef.current;
        if (!element) {
            return;
        }
        const { scrollHeight, clientHeight } = element;
        const maxScrollTop = scrollHeight - clientHeight;
        element.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
    }, [discussionRef]);

    const messageCount = getMessageCount(messages);
    const prevMessageCountRef = useRef(messageCount);

    const sendingCount = sendingMessageIds.length;
    const prevSendingCountRef = useRef(sendingCount);

    useEffect(() => {
        const prevMsgCount = prevMessageCountRef.current;
        const prevSndCount = prevSendingCountRef.current;

        prevMessageCountRef.current = messageCount;
        prevSendingCountRef.current = sendingCount;

        if (discussionRef.current === null) {
            return;
        }

        // Scroll to bottom after sending a message
        if (prevMsgCount > 0 && messageCount > 0 && prevMsgCount < messageCount) {
            scrollToBottom();
        }

        // Scroll to bottom when displaying a new draft
        if (prevSndCount < sendingCount) {
            scrollToBottom();
        }

        // Scroll to bottom when discussion first loads
        if (prevMsgCount === 0 && messageCount > 0) {
            scrollToBottom();
        }
    }, [messageCount, sendingCount, scrollToBottom, discussionRef]);

    return { scrollToBottom };
}
